import {
  ContentfulCollection,
  ContentfulGenericPage,
  ContentfulIcon,
  ContentfulNav,
  ContentfulNavDividerAndHeading,
  ContentfulNavMenu,
} from '@jbhi-fi/apis/src/contentful/types';
import React from 'react';
import { HeaderNavigationProps } from '../../HeaderLayout/components/HeaderNavigation/HeaderNavigation';
import { Icon } from '@jbhi-fi/lanyard-ui/components/Icon';
import { MenuItemWithSubMenu, BaseMenuItem, MenuItemType } from '../../Menu/types';
import { IconName } from '@jbhi-fi/lanyard-ui/components/Icon';

export abstract class NavTransformer {
  static hasFields = (
    item: ContentfulNavMenu | ContentfulNavDividerAndHeading | ContentfulCollection | ContentfulGenericPage
  ): boolean => Boolean(item.fields);

  static getIcon = (icon?: ContentfulIcon | IconName): React.ReactElement | null => {
    const iconName = typeof icon === 'string' ? icon : icon?.fields?.name || '';
    return iconName ? <Icon name={iconName} height={18} width={18} /> : null;
  };

  static handleNavDividerAndHeading = (
    item: ContentfulNavDividerAndHeading
  ): MenuItemWithSubMenu<MenuItemWithSubMenu<BaseMenuItem>> => {
    if (item.fields?.shouldDisplayHeading) {
      if (item?.fields?.shouldDisplayDivider) {
        return {
          type: MenuItemType.headingAndDivider,
          label: item.fields?.heading,
        };
      }
      return {
        type: MenuItemType.heading,
        label: item.fields?.heading,
      };
    }
    return {
      type: MenuItemType.divider,
    };
  };

  private static _getParentHandle = (item: ContentfulCollection): string | null => {
    if (!item.fields?.nestedCollection) return null;
    const parent = item.fields?.ancestorCollections?.[0];

    if (parent?.fields?.nestedCollection) {
      return this._getParentHandle(parent);
    }

    return parent?.fields?.handle ?? null;
  };

  static handleCollection = (item: ContentfulCollection): MenuItemWithSubMenu<MenuItemWithSubMenu<BaseMenuItem>> => {
    const parentHandle = this._getParentHandle(item);
    return {
      label: item.fields?.title,
      href: `/collections/${parentHandle ? `${parentHandle}/` : ''}${item.fields?.handle}`,
      type: MenuItemType.item,
      icon: this.getIcon(item.fields?.icon),
      isBold: false,
    };
  };

  static handleGenericPage = (item: ContentfulGenericPage): MenuItemWithSubMenu<MenuItemWithSubMenu<BaseMenuItem>> => {
    return {
      label: item.fields?.heading,
      href: `/pages/${item.fields?.urlSlug}`,
      type: MenuItemType.item,
      isBold: false,
    };
  };

  static handleNavMenu = (item: ContentfulNavMenu): MenuItemWithSubMenu<MenuItemWithSubMenu<BaseMenuItem>> => {
    const {
      url = '',
      heading = '',
      icon,
      navMenuItems,
      openInNewWindow,
      isHeadingBold,
      mainCollection,
      mainCollectionNavPrefix = 'All',
    } = (item as ContentfulNavMenu).fields ?? {};

    const defaultSubMenu: Array<BaseMenuItem> = mainCollection
      ? [
          {
            ...this.handleCollection(mainCollection),
            label: `${mainCollectionNavPrefix} ${mainCollection.fields?.title ?? ''}`,
            isBold: true,
            icon: null,
          },
        ]
      : [];
    const subMenu = navMenuItems ? navMenuItems.filter(this.hasFields).map(this.parseContentfulNavMenu) : [];

    return {
      type: MenuItemType.item,
      href: url,
      subMenu: [...defaultSubMenu, ...subMenu],
      shouldOpenInNewWindow: openInNewWindow,
      label: heading,
      icon: this.getIcon(icon),
      isBold: isHeadingBold,
    };
  };

  static parseContentfulNavMenu = (
    item: ContentfulNavMenu | ContentfulNavDividerAndHeading | ContentfulCollection | ContentfulGenericPage
  ): MenuItemWithSubMenu<MenuItemWithSubMenu<BaseMenuItem>> => {
    if (item.sys.contentType.sys.id === 'navDividerAndHeading') {
      return this.handleNavDividerAndHeading(item as ContentfulNavDividerAndHeading);
    }

    if (item.sys.contentType.sys.id === 'smartCollection') {
      return this.handleCollection(item as ContentfulCollection);
    }

    if (item.sys.contentType.sys.id === 'genericPage') {
      return this.handleGenericPage(item as ContentfulGenericPage);
    }

    return this.handleNavMenu(item as ContentfulNavMenu);
  };

  static parseMobileNavMenuItem = (item: ContentfulNavMenu): BaseMenuItem => {
    return {
      type: MenuItemType.item,
      label: item.fields?.heading,
      href: item.fields?.url,
      icon: this.getIcon(item.fields?.icon),
    };
  };

  static toHeaderNavigationProps = ({ fields }: ContentfulNav): HeaderNavigationProps => {
    const mobileNavMenuItems = fields?.mobileNavMenuItems ?? [];
    const navOptions = fields?.navMenuItems.map(item => {
      const { heading = '', navMenuItems = [], url } = item?.fields ?? {};
      return {
        label: heading,
        href: url,
        menuItems: navMenuItems.filter(this.hasFields).map(this.parseContentfulNavMenu),
      };
    });

    return {
      menuItems: navOptions ?? [],
      mobileNavMenuItems: mobileNavMenuItems.filter(this.hasFields)?.map(this.parseMobileNavMenuItem),
    };
  };
}
